import { useState } from "react";
import axios from "axios";
import { Share2, Download, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const W = 1080, H = 1080;

const drawScorecard = (goals, herbs, refCode) => {
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#0F3D2E";
  ctx.fillRect(0, 0, W, H);
  const grad = ctx.createRadialGradient(W / 2, 180, 60, W / 2, H / 2, 900);
  grad.addColorStop(0, "rgba(201,184,124,0.10)");
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = "#C9B87C";
  ctx.lineWidth = 3;
  ctx.strokeRect(36, 36, W - 72, H - 72);
  ctx.lineWidth = 1;
  ctx.strokeRect(48, 48, W - 96, H - 96);

  ctx.textAlign = "center";
  ctx.fillStyle = "#C9B87C";
  ctx.font = "44px 'Playfair Display', Georgia, serif";
  ctx.fillText("🌿", W / 2, 128);
  ctx.font = "bold 52px 'Playfair Display', Georgia, serif";
  ctx.fillText("Elite Herbalist", W / 2, 196);

  ctx.fillStyle = "#F5F0E6";
  ctx.font = "bold 62px 'Playfair Display', Georgia, serif";
  ctx.fillText("My Personalised", W / 2, 300);
  ctx.fillText("Herbal Wellness Plan", W / 2, 372);

  const goalText = `Goals: ${goals.slice(0, 3).join("  ·  ")}`;
  ctx.font = "30px Georgia, serif";
  const gw = ctx.measureText(goalText).width + 60;
  ctx.fillStyle = "rgba(201,184,124,0.18)";
  ctx.beginPath();
  ctx.roundRect((W - gw) / 2, 410, gw, 58, 29);
  ctx.fill();
  ctx.fillStyle = "#C9B87C";
  ctx.fillText(goalText, W / 2, 449);

  const list = herbs.slice(0, 6);
  const startY = 540;
  ctx.textAlign = "left";
  list.forEach((h, i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const x = col === 0 ? 110 : 570, y = startY + row * 110;
    ctx.fillStyle = "#C9B87C";
    ctx.beginPath();
    ctx.arc(x + 18, y - 12, 20, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#0F3D2E";
    ctx.font = "bold 24px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText(String(i + 1), x + 18, y - 3);
    ctx.textAlign = "left";
    ctx.fillStyle = "#F5F0E6";
    ctx.font = "bold 32px Georgia, serif";
    ctx.fillText(h.name.slice(0, 22), x + 54, y);
    ctx.fillStyle = "rgba(245,240,230,0.55)";
    ctx.font = "italic 22px Georgia, serif";
    ctx.fillText((h.scientificName || "").slice(0, 28), x + 54, y + 32);
  });

  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(245,240,230,0.8)";
  ctx.font = "28px Georgia, serif";
  ctx.fillText(`${herbs.length} herbs matched to my body by AI`, W / 2, 906);

  ctx.fillStyle = "#C9B87C";
  const footerText = refCode ? `Code ${refCode} = 10% off · eliteherbalist.com` : "Free AI assessment · eliteherbalist.com";
  ctx.font = "bold 30px Georgia, serif";
  const pw = Math.min(ctx.measureText(footerText).width + 70, W - 120);
  ctx.beginPath();
  ctx.roundRect((W - pw) / 2, 936, pw, 66, 33);
  ctx.fill();
  ctx.fillStyle = "#0F3D2E";
  ctx.fillText(footerText, W / 2, 979, pw - 40);

  return canvas;
};

const buildCaption = (refCode) =>
  `I just got my personalised herbal wellness plan from Elite Herbalist 🌿 Take your free AI assessment at eliteherbalist.com${refCode ? ` — use my code ${refCode} for 10% off the herb store!` : ""}`;

export const ShareScorecard = ({ goals, herbs }) => {
  const [open, setOpen] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);
  const [refCode, setRefCode] = useState(null);

  const generate = async () => {
    let code = refCode;
    if (!code) {
      try {
        const { data } = await axios.get(`${API}/store/referral/me`);
        code = data.code;
        setRefCode(code);
      } catch (e) {
        console.debug("Referral code unavailable:", e?.message);
      }
    }
    try { await document.fonts.ready; } catch (e) { console.debug("Font load wait skipped:", e?.message); }
    const canvas = drawScorecard(goals, herbs, code);
    setImgUrl(canvas.toDataURL("image/png"));
    setOpen(true);
  };

  const toBlob = async () => (await fetch(imgUrl)).blob();

  const handleShare = async () => {
    const blob = await toBlob();
    const file = new File([blob], "elite-herbalist-scorecard.png", { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], text: buildCaption(refCode) });
        return;
      } catch (e) {
        console.debug("Share cancelled or failed:", e?.message);
      }
    } else {
      handleDownload();
      toast.info("Image downloaded — attach it to your post!");
    }
  };

  const handleDownload = async () => {
    const link = document.createElement("a");
    link.href = imgUrl;
    link.download = "elite-herbalist-scorecard.png";
    link.click();
    toast.success("Scorecard image downloaded!");
  };

  const copyCaption = async () => {
    await navigator.clipboard.writeText(buildCaption(refCode));
    toast.success("Caption copied!");
  };

  return (
    <>
      <Button
        onClick={generate}
        variant="outline"
        className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full backdrop-blur-sm"
        data-testid="share-results-btn"
      >
        <Share2 className="mr-2 w-4 h-4" />
        Share My Results
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="share-scorecard-modal">
          <DialogHeader>
            <DialogTitle className="font-playfair">Share your scorecard</DialogTitle>
          </DialogHeader>
          {imgUrl && (
            <img src={imgUrl} alt="My Elite Herbalist scorecard" className="rounded-xl border border-gray-200 shadow-md" data-testid="share-scorecard-preview" />
          )}
          <div className="flex flex-wrap gap-2 justify-center pt-1">
            <Button onClick={handleShare} className="rounded-full" data-testid="share-native-btn">
              <Share2 className="mr-2 w-4 h-4" /> Share
            </Button>
            <Button onClick={handleDownload} variant="outline" className="rounded-full" data-testid="share-download-btn">
              <Download className="mr-2 w-4 h-4" /> Download
            </Button>
            <Button onClick={copyCaption} variant="outline" className="rounded-full" data-testid="share-copy-caption-btn">
              <Copy className="mr-2 w-4 h-4" /> Copy Caption
            </Button>
          </div>
          <p className="text-xs text-gray-400 text-center">
            {refCode
              ? `Your code ${refCode} is baked in — friends get 10% off their first order, and you earn a 15% reward code every time one buys.`
              : "Post it to Instagram, TikTok or your story — friends get their own free assessment at eliteherbalist.com"}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
